import axios from 'axios'

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000'
const API_BASE_PATH = import.meta.env.VITE_API_BASE_PATH || '/api'
const API_BASE_URL = `${API_URL}${API_BASE_PATH}/dashboard`
const JWT_STORAGE_KEY = import.meta.env.VITE_JWT_STORAGE_KEY || 'auth_token'

// Create axios instance with auth interceptor
const apiClient = axios.create()

// Add auth token to all requests
apiClient.interceptors.request.use((config) => {
  const token = (localStorage.getItem(JWT_STORAGE_KEY) || sessionStorage.getItem(JWT_STORAGE_KEY))
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

export const dashboardApi = {
  // Templates
  getTemplates: () => apiClient.get(`${API_BASE_URL}/templates/`),
  getTemplate: (id) => apiClient.get(`${API_BASE_URL}/templates/${id}/`),
  getTemplateLayout: (id) => apiClient.get(`${API_BASE_URL}/templates/${id}/layout/`),
  getProjectTemplates: (projectId) => apiClient.get(`${API_BASE_URL}/templates/available_for_project/`, { params: { project: projectId } }),
  // Canonical backend-defined templates (Overview / Parameter Comparison /
  // Project Map / Current Conditions / System Performance). Used by the Add
  // Dashboard popup so it doesn't list customer-added dashboards.
  getPredefinedTemplates: () => apiClient.get(`${API_BASE_URL}/templates/predefined/`),
  // Templates the *current user* can clone from in the Add Dashboard popup.
  // Honours the admin's TemplateAssignment rows (project-scoped + user-scoped).
  // Customer-created clones are excluded by the backend.
  getAdminAssignableTemplates: (projectId) =>
    apiClient.get(`${API_BASE_URL}/templates/available_admin_templates/`,
                  { params: { project: projectId } }),
  getWidgetsByCategory: () => apiClient.get(`${API_BASE_URL}/master-widgets/by_category/`),
  createTemplate: (data) => apiClient.post(`${API_BASE_URL}/templates/`, data),
  updateTemplate: (id, data) => apiClient.put(`${API_BASE_URL}/templates/${id}/`, data),
  partialUpdateTemplate: (id, data) => apiClient.patch(`${API_BASE_URL}/templates/${id}/`, data),
  deleteTemplate: (id) => apiClient.delete(`${API_BASE_URL}/templates/${id}/`),
  archiveTemplate: (id) => apiClient.post(`${API_BASE_URL}/templates/${id}/archive/`),
  activateTemplate: (id) => apiClient.post(`${API_BASE_URL}/templates/${id}/activate/`),
  duplicateTemplate: (id, data) => apiClient.post(`${API_BASE_URL}/templates/${id}/duplicate/`, data),
  assignTemplate: (templateId, data) => apiClient.post(`${API_BASE_URL}/templates/${templateId}/assign/`, data),
  getUserLayout: (templateId, projectId) => apiClient.get(`${API_BASE_URL}/templates/${templateId}/user_layout/`, { params: { project: projectId } }),
  saveUserLayout: (templateId, data) => apiClient.post(`${API_BASE_URL}/templates/${templateId}/save_user_layout/`, data),
  resetUserLayout: (templateId, data) => apiClient.post(`${API_BASE_URL}/templates/${templateId}/reset_user_layout/`, data),

  // Dashboards
  getDashboards: (projectId) => 
    apiClient.get(`${API_BASE_URL}/dashboards/`, { params: { project: projectId } }),
  getDashboard: (id) => apiClient.get(`${API_BASE_URL}/dashboards/${id}/`),
  createDashboard: (data) => apiClient.post(`${API_BASE_URL}/dashboards/`, data),
  updateDashboard: (id, data) => apiClient.put(`${API_BASE_URL}/dashboards/${id}/`, data),
  partialUpdateDashboard: (id, data) => apiClient.patch(`${API_BASE_URL}/dashboards/${id}/`, data),
  deleteDashboard: (id) => apiClient.delete(`${API_BASE_URL}/dashboards/${id}/`),
  resetDashboard: (id) => apiClient.post(`${API_BASE_URL}/dashboards/${id}/reset/`),
  archiveDashboard: (id) => apiClient.post(`${API_BASE_URL}/dashboards/${id}/archive/`),
  restoreDashboard: (id) => apiClient.post(`${API_BASE_URL}/dashboards/${id}/restore/`),
  duplicateDashboard: (id, data) => apiClient.post(`${API_BASE_URL}/dashboards/${id}/duplicate/`, data),

  // Widgets
  getWidgets: (dashboardId) => 
    apiClient.get(`${API_BASE_URL}/widgets/`, { params: { dashboard: dashboardId } }),
  getWidget: (id) => apiClient.get(`${API_BASE_URL}/widgets/${id}/`),
  createWidget: (data) => apiClient.post(`${API_BASE_URL}/widgets/`, data),
  updateWidget: (id, data) => apiClient.put(`${API_BASE_URL}/widgets/${id}/`, data),
  partialUpdateWidget: (id, data) => apiClient.patch(`${API_BASE_URL}/widgets/${id}/`, data),
  deleteWidget: (id) => apiClient.delete(`${API_BASE_URL}/widgets/${id}/`),
  reorderWidgets: (dashboardId, widgets) => 
    apiClient.post(`${API_BASE_URL}/widgets/reorder/`, { 
      dashboard_id: dashboardId, 
      widgets 
    }),
  updateWidgetConfiguration: (dashboardId, widgets) =>
    apiClient.post(`${API_BASE_URL}/widgets/configure/`, {
      dashboard_id: dashboardId,
      widgets
    }),

  // Preferences
  getPreferences: (projectId) => 
    apiClient.get(`${API_BASE_URL}/preferences/`, { params: { project: projectId } }),
  getPreference: (id) => apiClient.get(`${API_BASE_URL}/preferences/${id}/`),
  createPreference: (data) => apiClient.post(`${API_BASE_URL}/preferences/`, data),
  updatePreference: (id, data) => apiClient.put(`${API_BASE_URL}/preferences/${id}/`, data),
  partialUpdatePreference: (id, data) => apiClient.patch(`${API_BASE_URL}/preferences/${id}/`, data),

  // History
  getHistory: (dashboardId) => 
    apiClient.get(`${API_BASE_URL}/history/`, { params: { dashboard: dashboardId } }),

  // Master Widgets (read + patch rich_content from frontend)
  getMasterWidget: (id) => apiClient.get(`${API_BASE_URL}/master-widgets/${id}/`),
  updateMasterWidget: (id, data) => apiClient.patch(`${API_BASE_URL}/master-widgets/${id}/`, data),

  // Presentation mode (Symfony parity — see backend
  // DashboardViewSet.presentation_config)
  getPresentationConfig: (projectId) =>
    apiClient.get(`${API_BASE_URL}/dashboards/presentation-config/`, {
      params: { project: projectId },
    }),
  savePresentationConfig: (projectId, payload) =>
    apiClient.put(`${API_BASE_URL}/dashboards/presentation-config/`, {
      project: projectId,
      ...payload,
    }),

  // Auto-refresh when idle (project-level — see backend
  // DashboardViewSet.auto_refresh_config). Seconds; minimum 300 (5 min).
  getAutoRefreshConfig: (projectId) =>
    apiClient.get(`${API_BASE_URL}/dashboards/auto-refresh-config/`, {
      params: { project: projectId },
    }),
  saveAutoRefreshConfig: (projectId, seconds) =>
    apiClient.put(`${API_BASE_URL}/dashboards/auto-refresh-config/`, {
      project: projectId,
      auto_refresh_seconds: seconds,
    }),

  // Nav order — saves the user's preferred dashboard-menu order independently
  // of presentation-mode include/exclude (DashboardViewSet.nav_order).
  saveNavOrder: (projectId, templateIds) =>
    apiClient.put(`${API_BASE_URL}/dashboards/nav-order/`, {
      project: projectId,
      template_ids: templateIds,
    }),

  // Status
  getStatus: () => apiClient.get(`${API_BASE_URL}/status/`),
}

export default dashboardApi
